/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {dlsColor} from '../../theme/theme';

export enum Charge {
  Low = 20,
  Mid = 50,
  High = 100
}

/**
 * getChargeLevelColor - returns color of charge level
 * 
 * ```typescript
 * 
 * import {getChargeLevelColor} from './chargeLevelUtils'
 * 
 * getChargeLevelColor(20)  // returns caRed
 * getChargeLevelColor(50)  // returns caYellow
 * getChargeLevelColor(100) // returns caGreen
 * 
 * ```
 * @param charge - charge level
 * @returns - color of charge level
 */
export const getChargeLevelColor = (charge: number) => {
  if (charge <= Charge.Low) {
    return dlsColor('caRed');
  }
  if (charge <= Charge.Mid) {
    return dlsColor('caYellow');
  }
  if (charge >= Charge.Mid) {
    return dlsColor('caGreen');
  }
};

/**
 * validateChargeLevel - returns valid charge level
 * 
 * ```typescript
 * 
 * import {validateChargeLevel} from './chargeLevelUtils'
 * 
 * validateChargeLevel(0)    // returns 0
 * validateChargeLevel(100)  // returns 100
 * validateChargeLevel(-100) // returns 0
 * validateChargeLevel(200)  // returns 100
 * 
 * ```
 * @param charge - charge level
 * @returns - valid charge level
 */
export const validateChargeLevel = (charge: number) => {
  if (charge > 100) {
    return 100;
  }
  if (charge < 0) {
    return 0;
  }
  return charge;
};

/**
 * isBatteryValid is a helper function to check if battery is valid.
 * 
 * ```typescript
 * 
 * import {isBatteryValid} from './chargeLevelUtils'
 * 
 * isBatteryValid(0)    // returns false
 * isBatteryValid(100)  // returns true
 * 
 * ```
 * @param battery: number - battery percentage
 * @returns - true if battery is valid
 */
export const isBatteryValid = (battery: number) => {
  return !isNil(battery) && battery >= 1 && battery <= 100;
};

/**
 * validateBatteryPercentage is a helper function to validate battery percentage
 * to determine which battery percentage to display.
 * 
 * ```typescript
 * 
 * import {validateBatteryPercentage} from './chargeLevelUtils'
 * 
 * validateBatteryPercentage(0, 0)    // returns null
 * validateBatteryPercentage(0, 100)  // returns 100
 * validateBatteryPercentage(100, 0)  // returns 100
 * 
 * ```
 * 
 * @param evBattery: number - ev battery percentage
 * @param deviceBattery: number - device battery percentage
 * @returns - ev battery percentage if it is valid, otherwise device battery percentage if it is valid, otherwise null
 */
export const validateBatteryPercentage = (evBattery: number, deviceBattery: number) => {
  if (isBatteryValid(evBattery)) {
    return evBattery;
  }
  if (isBatteryValid(deviceBattery)) {
    return deviceBattery;
  }
  return null;
};
